import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import TvRoundedIcon from '@mui/icons-material/TvRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { School } from 'models/school';
import { Tournament } from 'models/tournament';
import { getSchoolTournament } from 'services/school';
import { getAgesGroupFormatted } from 'helpers/accessors';
import { getOfficialWebsiteLink, getTournamentTVPublicSiteLink } from 'helpers/link';
import { getTournamentBookingLink } from 'helpers/tournament';
import { CLUB_GENDER_SERVER_TO_CLIENT_MAPPING } from 'consts/club';
import { SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'consts/sport';
import { GENDER_TYPE } from 'types/common';
import { SPORT_GENDER_TYPE } from 'types/sport';
import Loader from 'components/loader/loader';
import EmailContactActions from 'components/EmailContactActions';
import GoverningBodies from 'views/School/Tournaments/TournamentDetails/GoverningBodies';
import Sponsors from './Sponsors';
import './SchoolTournamentView.scss';

interface Props { school: School; }
type Tab = 'details' | 'participants' | 'documents' | 'governing' | 'sponsors';

const formatDate = (value: string) => new Intl.DateTimeFormat('en-GB', {
   weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
}).format(new Date(value));

const formatTime = (value: string) => new Intl.DateTimeFormat('en-GB', {
   hour: '2-digit', minute: '2-digit'
}).format(new Date(value));

const formatDateTime = (value: string) => new Intl.DateTimeFormat('en-GB', {
   day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
}).format(new Date(value));

const attachmentUrl = (value: string) => {
   const url = String(value || '').trim();
   return /^(https?:)?\/\//i.test(url) ? url : `${window.apiBaseFile}${url}`;
};

export default function SchoolTournamentView({ school }: Props): JSX.Element {
   const { id = '' } = useParams();
   const navigate = useNavigate();
   const [tournament, setTournament] = useState<Tournament>();
   const [isLoading, setIsLoading] = useState(true);
   const [loadError, setLoadError] = useState(false);
   const [tab, setTab] = useState<Tab>('details');
   const [activePhoto, setActivePhoto] = useState(0);

   useEffect(() => {
      setIsLoading(true);
      setLoadError(false);
      getSchoolTournament(school.id, id)
         .then(setTournament)
         .catch(() => { setTournament(undefined); setLoadError(true); })
         .finally(() => setIsLoading(false));
   }, [id, school.id]);

   const booking = useMemo(() => {
      if (!tournament) return { open: false, wait: false, upcoming: false };
      const now = Date.now();
      const opensAt = tournament.entriesOpen ? new Date(tournament.entriesOpen).getTime() : 0;
      const closesAt = tournament.deadlineForEntries ? new Date(tournament.deadlineForEntries).getTime() : 0;
      const open = Boolean(opensAt && closesAt) && opensAt <= now && closesAt >= now;
      const upcoming = Boolean(opensAt) && opensAt > now;
      const finiteSettings = tournament.autoEnrollmentTeamsSettings || [];
      const hasSpace = finiteSettings.some(item => !item.maximumNumberTeams || (item.currentNumberTeams || 0) < item.maximumNumberTeams);
      return { open, wait: open && (!hasSpace || tournament.isAutoEnrollmentToWaitingList), upcoming };
   }, [tournament]);

   if (isLoading) return <Loader />;
   if (!tournament) return <div className='union-tournament-empty' role={loadError ? 'alert' : undefined}><EmojiEventsRoundedIcon /><h1>{loadError ? 'Tournament could not be loaded' : 'Tournament not found'}</h1><p>Please check the link or return to the tournament list.</p><button type='button' onClick={() => navigate('/upcomingTournaments')}>View tournaments</button></div>;

   const venueName = (tournament.venue as any)?.placeData?.name || tournament.school?.name || 'Venue to be confirmed';
   const postcode = tournament.venue?.postcodeData?.postcode || tournament.venue?.postcode || '';
   const gender = CLUB_GENDER_SERVER_TO_CLIENT_MAPPING[tournament.gender as GENDER_TYPE] ||
      SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[tournament.gender as SPORT_GENDER_TYPE] || '';
   const publicPhotos = (tournament.photos || []).map(photo => photo.picUrl).filter(Boolean);
   const datesAreSame = new Date(tournament.startTime).toDateString() === new Date(tournament.endTime).toDateString();
   const officialUrl = getOfficialWebsiteLink(tournament.link);
   const tournamentTvStatus = tournament.publicBigscreenSite?.status;
   const tournamentTvUrl = tournament.domain && (tournamentTvStatus === 'PUBLIC_AVAILABLE' || tournamentTvStatus === 'PROTECTED')
      ? getTournamentTVPublicSiteLink(tournament)
      : '';
   const organiserName = String(tournament.organisers || '').trim() || tournament.school?.name || school.name;
   const visibleAnnouncements = (tournament.announcements || []).filter(item => {
      const attachments = (item as any).attachments || {};
      return Boolean(String(item.title || '').trim() || String(item.body || '').trim() ||
         (attachments.images || []).length || (attachments.files || []).length);
   });
   const visibleGoverningBodies = (tournament.nationalGoverningBodies || []).filter(item =>
      Boolean(String(item.name || '').trim() || String(item.picUrl || '').trim() || String(item.link || '').trim())
   );
   const visibleSponsors = (tournament.sponsors || []).filter(item =>
      Boolean(String(item.name || '').trim() || String(item.picUrl || '').trim() || String(item.link || '').trim())
   );
   const showPhoto = (offset: number) => setActivePhoto(current => (current + offset + publicPhotos.length) % publicPhotos.length);

   return (
      <article className='union-tournament'>
         <button className='union-tournament__back' type='button' onClick={() => navigate('/upcomingTournaments')}>
            <ArrowBackRoundedIcon /> Back to tournaments
         </button>

         {publicPhotos.length > 0 && (
            <div className='union-tournament__gallery'>
               <img src={publicPhotos[activePhoto]} alt={`Tournament photo ${activePhoto + 1} of ${publicPhotos.length}`} />
               <div className='union-tournament__gallery-shade' />
               {publicPhotos.length > 1 && <><button className='is-previous' type='button' onClick={() => showPhoto(-1)} aria-label='Previous photo'><ChevronLeftRoundedIcon /></button><button className='is-next' type='button' onClick={() => showPhoto(1)} aria-label='Next photo'><ChevronRightRoundedIcon /></button><span className='union-tournament__gallery-count'>{activePhoto + 1} / {publicPhotos.length}</span></>}
            </div>
         )}

         <header className={`union-tournament__hero${publicPhotos.length ? ' has-gallery' : ''}`}>
            <div>
               <span className='union-tournament__eyebrow'><EmojiEventsRoundedIcon /> {tournament.sport?.name || 'Tournament'}</span>
               <h1>{tournament.name}</h1>
               <p>Hosted by {organiserName}</p>
            </div>
            <div className='union-tournament__actions'>
               {officialUrl && <a href={officialUrl} target='_blank' rel='noreferrer'>Official website <OpenInNewRoundedIcon /></a>}
               {tournamentTvUrl && <a href={tournamentTvUrl} target='_blank' rel='noreferrer'><TvRoundedIcon /> Tournament TV <OpenInNewRoundedIcon /></a>}
               {booking.open && <a className='is-primary' href={getTournamentBookingLink(tournament.id)} target='_blank' rel='noreferrer'>{booking.wait ? 'Book onto waiting list' : 'Book now'}</a>}
            </div>
         </header>

         <section className='union-tournament__facts'>
            <div><CalendarMonthRoundedIcon /><span>Date<strong>{formatDate(tournament.startTime)}</strong></span></div>
            <div><ScheduleRoundedIcon /><span>Time<strong>{formatTime(tournament.startTime)}–{formatTime(tournament.endTime)}</strong></span></div>
            <div><LocationOnRoundedIcon /><span>Venue<strong>{venueName}{postcode ? `, ${postcode}` : ''}</strong></span></div>
            <div><PeopleAltRoundedIcon /><span>Entry<strong>{[gender, getAgesGroupFormatted(tournament, school)].filter(Boolean).join(' · ')}</strong></span></div>
         </section>

         <nav className='union-tournament__tabs' aria-label='Tournament information'>
            <button className={tab === 'details' ? 'is-active' : ''} onClick={() => setTab('details')}>Overview</button>
            {(tournament.schools || []).length > 0 && <button className={tab === 'participants' ? 'is-active' : ''} onClick={() => setTab('participants')}>Schools entered</button>}
            {visibleAnnouncements.length > 0 && <button className={tab === 'documents' ? 'is-active' : ''} onClick={() => setTab('documents')}>Updates & documents</button>}
            {visibleGoverningBodies.length > 0 && <button className={tab === 'governing' ? 'is-active' : ''} onClick={() => setTab('governing')}>Governing bodies</button>}
            {visibleSponsors.length > 0 && <button className={tab === 'sponsors' ? 'is-active' : ''} onClick={() => setTab('sponsors')}>Sponsors</button>}
         </nav>

         <div className='union-tournament__content'>
            {tab === 'details' && <>
               {booking.wait && <section className='union-tournament__waiting-list'>
                  <span className='union-tournament__waiting-list-icon'><HourglassTopRoundedIcon /></span>
                  <div>
                     <span>Waiting list</span>
                     <h2>Teams currently waiting</h2>
                     <p>Once spaces are full, you will be placed onto a waiting list and contacted when a place becomes available.</p>
                  </div>
                  <strong>{tournament.waitingListCount || 0}</strong>
               </section>}
               <section className='union-tournament__panel'>
                  <h2>About this tournament</h2>
                  {tournament.description
                     ? <div className='union-tournament__description' dangerouslySetInnerHTML={{ __html: tournament.description }} />
                     : <p>Full tournament information will be published here when it becomes available.</p>}
               </section>
               <aside className='union-tournament__panel union-tournament__entry'>
                  <h2>Entry information</h2>
                  {(booking.upcoming || booking.open) && <div className={`union-tournament__booking-date${booking.open ? ' is-open' : ''}`}>
                     <ScheduleRoundedIcon />
                     <span>{booking.open ? 'Booking closes' : 'Booking opens'}<strong>{formatDateTime(booking.open ? tournament.deadlineForEntries : tournament.entriesOpen)}</strong></span>
                  </div>}
                  <dl>
                     <div><dt>Entries open</dt><dd>{tournament.entriesOpen ? formatDate(tournament.entriesOpen) : 'To be confirmed'}</dd></div>
                     <div><dt>Entries close</dt><dd>{tournament.deadlineForEntries ? formatDate(tournament.deadlineForEntries) : 'To be confirmed'}</dd></div>
                     <div><dt>Organiser</dt><dd>{organiserName}</dd></div>
                  </dl>
                  {(tournament.organisersEmail || school.email) && <EmailContactActions email={tournament.organisersEmail || school.email} emailActionLabel='Contact the organiser' />}
                  {booking.open && <a className='is-book' href={getTournamentBookingLink(tournament.id)} target='_blank' rel='noreferrer'>{booking.wait ? 'Book onto waiting list' : 'Book now'}</a>}
               </aside>
            </>}
            {tab === 'documents' && visibleAnnouncements.length > 0 && <section className='union-tournament__panel is-wide union-tournament__resource-panel'><header><span>Latest information</span><h2>Updates &amp; documents</h2><p>News, notices and downloadable resources from the tournament organisers.</p></header><div className='union-tournament__updates'>{visibleAnnouncements.map((announcement, index) => {
               const attachments = (announcement as any).attachments || {};
               const images = (attachments.images || []).filter(item => item && item.url);
               const files = (attachments.files || []).filter(item => item && item.url);
               return <article className='union-tournament__update' key={(announcement as any)._id || index}>
                  {images.length > 0 && <img src={attachmentUrl(images[0].url)} alt='' />}
                  <div className='union-tournament__update-content'>
                     <div className='union-tournament__update-meta'><span>Update</span>{(announcement as any).date && <time>{formatDate((announcement as any).date)}</time>}</div>
                     <h3>{announcement.title || 'Tournament update'}</h3>
                     {announcement.body && <div className='union-tournament__update-body' dangerouslySetInnerHTML={{ __html: announcement.body }} />}
                     {files.length > 0 && <div className='union-tournament__update-files'>{files.map((file, fileIndex) => <a href={attachmentUrl(file.url)} target='_blank' rel='noreferrer' key={file._id || fileIndex}><DescriptionOutlinedIcon /><span><strong>{file.name || 'Document'}</strong><small>Open or download</small></span><DownloadRoundedIcon /></a>)}</div>}
                  </div>
               </article>;
            })}</div></section>}
            {tab === 'governing' && visibleGoverningBodies.length > 0 && <section className='union-tournament__panel is-wide union-tournament__resource-panel'><header><span>Competition partners</span><h2>Governing bodies</h2><p>Official organisations associated with this tournament.</p></header><div className='union-tournament__embedded is-governing'><GoverningBodies tournament={{ ...tournament, nationalGoverningBodies: visibleGoverningBodies }} school={school} /></div></section>}
            {tab === 'sponsors' && visibleSponsors.length > 0 && <section className='union-tournament__panel is-wide union-tournament__resource-panel'><header><span>Our partners</span><h2>Sponsors</h2><p>Organisations supporting this tournament.</p></header><Sponsors sponsors={visibleSponsors} /></section>}
            {tab === 'participants' && <section className='union-tournament__panel is-wide'><div className='union-tournament__participants-heading'><div><h2>Schools entered</h2><p>Schools currently participating in this tournament.</p></div><strong>{tournament.schools.length}</strong></div><div className='union-tournament__participants'>{tournament.schools.map(participant => <article key={participant.id}>{participant.pic ? <img src={participant.pic} alt='' /> : <PeopleAltRoundedIcon />}<div><strong>{participant.name}</strong>{participant.postcode?.postcode && <span>{participant.postcode.postcode}</span>}</div></article>)}</div></section>}
         </div>
      </article>
   );
}
